import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { T, card, tbl, th, td } from '../lib/theme'

const fmt = (n) => '₦' + (Number(n)||0).toLocaleString(undefined,{maximumFractionDigits:0})

export default function TargetSettings({ session, roleCtx }) {
  const role    = roleCtx?.role || 'guest'
  const isAdmin = role === 'admin'

  const [superAgents, setSuperAgents] = useState([])
  const [targets,     setTargets]     = useState({})
  const [overrides,   setOverrides]   = useState([])
  const [season,      setSeason]      = useState('dry')
  const [loading,     setLoading]     = useState(true)
  const [saving,      setSaving]      = useState(null)
  const [clearing,    setClearing]    = useState(null)
  const [editRow,     setEditRow]     = useState(null)
  const [msg,         setMsg]         = useState(null)
  const [filterLga,    setFilterLga]   = useState('')
  const [filterSA,     setFilterSA]    = useState('')
  const [filterStatus, setFilterStatus]= useState('Active')
  const [lgas,         setLgas]        = useState([])
  const [lgaMap,       setLgaMap]      = useState({})  // id→{security_level}
  const [histSaId,     setHistSaId]    = useState(null)
  const [history,      setHistory]     = useState([])

  const selS = {
    fontSize:12, fontFamily:T.font, background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6, padding:'7px 12px',
    color:T.txtPrimary, cursor:'pointer',
  }
  const inputS = {
    width:'100%', padding:'8px 10px', background:T.surface3,
    border:`1px solid ${T.border}`, borderRadius:6,
    fontSize:12, fontFamily:T.font, color:T.txtPrimary, outline:'none',
  }

  const load = useCallback(async () => {
    const [
      { data:sa }, { data:tg }, { data:ov },
      { data:se }, { data:lg }
    ] = await Promise.all([
      supabase.from('super_agents')
        .select('*, lgas(name,tier,security_level), agents!agents_super_agent_id_fkey(id,agent_class)')
        .order('company_name'),
      supabase.from('weekly_targets').select('*').order('effective_from',{ascending:false}).limit(10),
      supabase.from('target_overrides').select('*').is('cleared_at', null),
      supabase.from('app_settings').select('*'),
      supabase.from('lgas').select('id,name,security_level').order('name'),
    ])
    setSuperAgents(sa||[])
    // Build lgaMap for security level lookup
    const lm = {}
    ;(lg||[]).forEach(l => { lm[l.id] = l })
    setLgaMap(lm)
    const tm = {}
    ;(tg||[]).forEach(t => {
      if (!t.tier) return
      if (!tm[t.tier]) tm[t.tier] = {}
      if (!tm[t.tier][t.agent_class]) tm[t.tier][t.agent_class] = t.dry_season_target
    })
    setTargets(tm)
    setOverrides(ov||[])
    if (se) { const s=se.find(x=>x.key==='season'); if(s) setSeason(s.value) }
    setLgas(lg||[])
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  function formulaTarget(sa) {
    const seasonFactor = season === 'rain' ? 0.75 : 1.0
    const secLevel     = sa.lgas?.security_level || 'Green'
    const secFactor    = secLevel === 'Red' ? 0.75 : 1.0   // Dangerous = −25% independent of season
    const tier         = sa.lgas?.tier || 'A'
    const agentList    = sa.agents || []
    if (agentList.length === 0) return 0
    const cls          = sa.agent_class || agentList[0]?.agent_class || '1'
    const base         = (targets[tier]?.[cls] || targets['A']?.[cls] || 120000) * seasonFactor * secFactor
    return base * agentList.length
  }

  function isDangerousLga(sa) {
    return (sa.lgas?.security_level || 'Green') === 'Red'
  }

  function activeOverride(saId) {
    return overrides.find(o => o.super_agent_id === saId) || null
  }

  async function saveOverride(saId) {
    if (!editRow || editRow.saId !== saId) return
    const amount = Number(editRow.amount)
    if (!amount || amount <= 0) { setMsg({ type:'err', text:'Enter a valid override amount.' }); return }
    setSaving(saId); setMsg(null)
    const existing = activeOverride(saId)
    if (existing) {
      await supabase.from('target_overrides').update({ cleared_at: new Date().toISOString() }).eq('id', existing.id)
    }
    const { error } = await supabase.from('target_overrides').insert({
      super_agent_id: saId,
      override_amount: amount,
      notes: editRow.notes || null,
      valid_from: new Date().toISOString(),
      set_by: session?.user?.email || 'admin',
    })
    setSaving(null)
    if (error) { setMsg({ type:'err', text: error.message }); return }
    setMsg({ type:'ok', text: 'Override saved.' })
    setEditRow(null)
    load()
  }

  async function clearOverride(saId) {
    const existing = activeOverride(saId)
    if (!existing) return
    setClearing(saId); setMsg(null)
    await supabase.from('target_overrides').update({ cleared_at: new Date().toISOString() }).eq('id', existing.id)
    setClearing(null)
    setMsg({ type:'ok', text: 'Override cleared — SA reverted to formula target.' })
    load()
  }

  async function loadHistory(saId) {
    if (histSaId === saId) { setHistSaId(null); setHistory([]); return }
    const { data } = await supabase.from('target_overrides')
      .select('*').eq('super_agent_id', saId).order('valid_from', { ascending: false })
    setHistory(data||[])
    setHistSaId(saId)
  }

  const filteredSAs = superAgents.filter(sa => {
    if (filterLga    && sa.lgas?.name !== filterLga) return false
    if (filterSA     && sa.id !== filterSA)          return false
    if (filterStatus && sa.status !== filterStatus)  return false
    return true
  })

  if (loading) return <div style={{ color:T.txtSecondary, fontSize:13 }}>Loading target settings…</div>

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:20, fontWeight:800, color:T.txtPrimary, marginBottom:4 }}>Target Settings</div>
        <div style={{ fontSize:12, color:T.txtSecondary }}>
          Set or clear per-SA weekly target overrides · Overrides carry forward until manually cleared ·{' '}
          {isAdmin ? 'Admin: edit enabled' : 'View only — contact Admin to change targets'}
        </div>
      </div>

      {msg && (
        <div style={{
          padding:'10px 14px', borderRadius:6, marginBottom:16, fontSize:12,
          background: msg.type==='ok' ? 'rgba(63,185,80,.12)' : 'rgba(248,81,73,.12)',
          border: `1px solid ${msg.type==='ok' ? 'rgba(63,185,80,.3)' : 'rgba(248,81,73,.3)'}`,
          color: msg.type==='ok' ? '#3fb950' : '#f85149',
        }}>{msg.text}</div>
      )}

      <div style={{ display:'flex', gap:12, marginBottom:18, flexWrap:'wrap', alignItems:'center' }}>
        {[
          { label:'LG Target', color:T.txtSecondary, bg:T.surface3, border:T.border,
            desc:'Formula target (tier × class × season × agents)' },
          { label:'Override', color:'#E8A020', bg:'rgba(232,160,32,.12)', border:'rgba(232,160,32,.3)',
            desc:'Manual override — carries forward until cleared' },
        ].map(({ label, color, bg, border, desc }) => (
          <div key={label} style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ padding:'2px 10px', borderRadius:20, fontSize:10, fontWeight:700,
              color, background:bg, border:`1px solid ${border}` }}>{label}</span>
            <span style={{ fontSize:11, color:T.txtMuted }}>{desc}</span>
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
        <select style={selS} value={filterLga} onChange={e=>{ setFilterLga(e.target.value); setFilterSA('') }}>
          <option value="">All LGAs</option>
          {lgas.map(l => (
            <option key={l.id} value={l.name}>
              {l.security_level === 'Red' ? '⚠ ' : ''}{l.name}
            </option>
          ))}
        </select>
        <select style={selS} value={filterSA} onChange={e=>setFilterSA(e.target.value)}>
          <option value="">All Super Agents</option>
          {superAgents
            .filter(sa => !filterLga || sa.lgas?.name === filterLga)
            .map(sa => <option key={sa.id} value={sa.id}>{sa.company_name}</option>)}
        </select>
        <select style={selS} value={filterStatus} onChange={e=>setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Suspended">Suspended</option>
        </select>
        <span style={{ fontSize:11, color:T.txtMuted }}>
          {filteredSAs.length} SA{filteredSAs.length!==1?'s':''} ·{' '}
          {overrides.length} override{overrides.length!==1?'s':''} active ·{' '}
          Season: <strong style={{ color: season==='dry'?'#E8A020':'#1E90FF' }}>
            {season==='dry'?'☀ Dry':'🌧 Rainy −25%'}
          </strong>
          {lgas.some(l=>l.security_level==='Red') && (
            <span style={{ marginLeft:8, color:'#f85149', fontWeight:700 }}>
              · ⚠ {lgas.filter(l=>l.security_level==='Red').length} LGA(s) Dangerous
            </span>
          )}
        </span>
      </div>

      <div style={card}>
        <table style={tbl}>
          <thead><tr>
            {[
              'Super Agent','LGA','Agents','Formula Target',
              'Override Target','Per-Agent','Status','Notes','History',
              ...(isAdmin?['Actions']:[])
            ].map(h => <th key={h} style={th}>{h}</th>)}
          </tr></thead>
          <tbody>
            {filteredSAs.map(sa => {
              const ov        = activeOverride(sa.id)
              const formula   = formulaTarget(sa)
              const agentCnt  = (sa.agents||[]).length
              const effective = ov ? ov.override_amount : formula
              const perAgent  = agentCnt > 0 ? Math.round(effective / agentCnt) : 0
              const isEditing = editRow?.saId === sa.id

              const dangerous = isDangerousLga(sa)

              return (
                <React.Fragment key={sa.id}>
                  <tr style={{ background: isEditing ? 'rgba(232,160,32,.06)' : dangerous ? 'rgba(248,81,73,.03)' : 'transparent' }}>
                    <td style={{ ...td, fontWeight:600 }}>{sa.company_name}</td>
                    <td style={{ ...td, color:T.txtSecondary }}>
                      {sa.lgas?.name||'—'}
                      {dangerous && (
                        <span title="Security level set to Dangerous — target reduced by 25%"
                          style={{ marginLeft:6, fontSize:9, color:'#f85149', fontWeight:700,
                            background:'rgba(248,81,73,.12)', border:'1px solid rgba(248,81,73,.3)',
                            borderRadius:4, padding:'1px 5px', cursor:'help' }}>
                          ⚠ DANGER
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign:'center', color:T.txtSecondary }}>{agentCnt}</td>
                    <td style={{ ...td, color: dangerous ? '#f85149' : T.txtSecondary }}
                      title={dangerous ? 'Target reduced 25% due to Dangerous security level' : undefined}>
                      {fmt(formula)}
                      {dangerous && <span style={{ fontSize:9, color:'#f85149', display:'block' }}>−25% security</span>}
                    </td>
                    <td style={{ ...td, fontWeight:ov?700:400, color:ov?'#E8A020':T.txtMuted }}>
                      {ov ? fmt(ov.override_amount) : '—'}
                    </td>
                    <td style={{ ...td, color:T.txtSecondary }}>{fmt(perAgent)}</td>
                    <td style={td}>
                      {ov
                        ? <span style={{ padding:'2px 10px', borderRadius:20, fontSize:10, fontWeight:700,
                            color:'#E8A020', background:'rgba(232,160,32,.12)',
                            border:'1px solid rgba(232,160,32,.3)' }}>Override</span>
                        : <span style={{ padding:'2px 10px', borderRadius:20, fontSize:10, fontWeight:700,
                            color:T.txtSecondary, background:T.surface3,
                            border:`1px solid ${T.border}` }}>LG Target</span>
                      }
                    </td>
                    <td style={{ ...td, color:T.txtSecondary, fontSize:11, maxWidth:180 }}>
                      {ov?.notes||'—'}
                    </td>
                    <td style={td}>
                      <button onClick={()=>loadHistory(sa.id)}
                        style={{ padding:'3px 10px', background:'transparent', color:'#1E90FF',
                          border:'1px solid rgba(30,144,255,.3)', borderRadius:5,
                          fontSize:11, fontFamily:T.font, cursor:'pointer' }}>
                        {histSaId===sa.id ? 'Hide' : 'History'}
                      </button>
                    </td>
                    {isAdmin && (
                      <td style={{ ...td, whiteSpace:'nowrap' }}>
                        {isEditing ? (
                          <div style={{ display:'flex', gap:6 }}>
                            <button onClick={()=>saveOverride(sa.id)} disabled={saving===sa.id}
                              style={{ padding:'4px 12px', background:'#1E90FF', color:'#fff',
                                border:'none', borderRadius:5, fontSize:11, fontWeight:700,
                                fontFamily:T.font, cursor:'pointer' }}>
                              {saving===sa.id ? '…' : 'Save'}
                            </button>
                            <button onClick={()=>setEditRow(null)}
                              style={{ padding:'4px 10px', background:T.surface3, color:T.txtSecondary,
                                border:`1px solid ${T.border}`, borderRadius:5,
                                fontSize:11, fontFamily:T.font, cursor:'pointer' }}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div style={{ display:'flex', gap:6 }}>
                            <button
                              onClick={()=>setEditRow({ saId:sa.id, amount:ov?.override_amount||Math.round(formula), notes:ov?.notes||'' })}
                              style={{ padding:'4px 12px', background:'rgba(232,160,32,.12)', color:'#E8A020',
                                border:'1px solid rgba(232,160,32,.3)', borderRadius:5,
                                fontSize:11, fontWeight:700, fontFamily:T.font, cursor:'pointer' }}>
                              {ov ? 'Edit' : 'Override'}
                            </button>
                            {ov && (
                              <button onClick={()=>clearOverride(sa.id)} disabled={clearing===sa.id}
                                style={{ padding:'4px 10px', background:'rgba(248,81,73,.08)', color:'#f85149',
                                  border:'1px solid rgba(248,81,73,.2)', borderRadius:5,
                                  fontSize:11, fontFamily:T.font, cursor:'pointer' }}>
                                {clearing===sa.id ? '…' : 'Clear'}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}
                  </tr>

                  {isEditing && (
                    <tr style={{ background:'rgba(232,160,32,.04)' }}>
                      <td colSpan={isAdmin?10:9} style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
                        <div style={{ display:'flex', gap:12, alignItems:'flex-end', flexWrap:'wrap' }}>
                          <div style={{ flex:'0 0 200px' }}>
                            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted,
                              textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>
                              Override Amount (₦)
                            </div>
                            <input type="number" style={inputS}
                              value={editRow.amount}
                              onChange={e=>setEditRow(r=>({...r, amount:e.target.value}))}
                              placeholder="e.g. 2400000" />
                          </div>
                          <div style={{ flex:'1', minWidth:200 }}>
                            <div style={{ fontSize:10, fontWeight:600, color:T.txtMuted,
                              textTransform:'uppercase', letterSpacing:'.07em', marginBottom:4 }}>
                              Reason / Notes
                            </div>
                            <input type="text" style={inputS}
                              value={editRow.notes}
                              onChange={e=>setEditRow(r=>({...r, notes:e.target.value}))}
                              placeholder="e.g. Sparse market — ~40 traders in zone" />
                          </div>
                          <div style={{ fontSize:11, color:T.txtMuted, paddingBottom:8 }}>
                            Formula: {fmt(formula)} · Per-agent if saved:{' '}
                            <strong style={{ color:T.txtPrimary }}>
                              {fmt(Number(editRow.amount)>0 ? Math.round(Number(editRow.amount)/(agentCnt||1)) : 0)}
                            </strong>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {histSaId===sa.id && (
                    <tr>
                      <td colSpan={isAdmin?10:9} style={{ padding:'0 16px 14px',
                        borderBottom:`1px solid ${T.border}`, background:'rgba(30,144,255,.03)' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#1E90FF', margin:'10px 0 8px' }}>
                          Override history — {sa.company_name}
                        </div>
                        {history.length===0
                          ? <div style={{ fontSize:11, color:T.txtMuted }}>No override history for this SA.</div>
                          : (
                            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                              <thead><tr>
                                {['Set On','Amount','Notes','Set By','Status'].map(h =>
                                  <th key={h} style={{ ...th, fontSize:9, padding:'5px 10px' }}>{h}</th>
                                )}
                              </tr></thead>
                              <tbody>
                                {history.map(h => (
                                  <tr key={h.id}>
                                    <td style={{ ...td, fontSize:11, padding:'5px 10px' }}>
                                      {h.valid_from ? new Date(h.valid_from).toLocaleDateString('en-NG') : '—'}
                                    </td>
                                    <td style={{ ...td, fontSize:11, padding:'5px 10px', fontWeight:700, color:'#E8A020' }}>
                                      {fmt(h.override_amount)}
                                    </td>
                                    <td style={{ ...td, fontSize:11, padding:'5px 10px', color:T.txtSecondary }}>
                                      {h.notes||'—'}
                                    </td>
                                    <td style={{ ...td, fontSize:11, padding:'5px 10px', color:T.txtSecondary }}>
                                      {h.set_by||'—'}
                                    </td>
                                    <td style={{ ...td, fontSize:11, padding:'5px 10px' }}>
                                      {h.cleared_at
                                        ? <span style={{ color:'#f85149' }}>
                                            Cleared {new Date(h.cleared_at).toLocaleDateString('en-NG')}
                                          </span>
                                        : <span style={{ color:'#3fb950' }}>Active</span>
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )
                        }
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
            {filteredSAs.length===0 && (
              <tr><td colSpan={isAdmin?10:9} style={{ ...td, textAlign:'center', color:T.txtMuted }}>
                No Super Agents found.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
